/**
 * S2 (AB#2205, SG-07 / SG-08) — a staff member's own schedule, read-only:
 * weekly working hours (per branch), upcoming approved time off and salon
 * closures — exactly what GET /salon-staff/my-schedule returns (the same data
 * the owner sees in Manage Staff). Changes are made by the salon owner.
 */
import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useStaffMe, useStaffMySchedule } from '../../../hooks/useStaffSelf';
import {
    absenceTypeLabel,
    fill,
    formatDayDate,
    localizedName,
    timeRange,
    weekdayName,
} from '../../../lib/staffDisplay';
import type { StaffScheduleAbsence } from '../../../types/staffSelf';
import s from './MySchedule.module.css';

type TFn = (key: string) => string;

const DATE_OPTS: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };

function absenceWhen(a: StaffScheduleAbsence, language: string, t: TFn, branchName: (id: number) => string): string {
    const start = formatDayDate(a.startDate, language, DATE_OPTS);
    const end = formatDayDate(a.endDate, language, DATE_OPTS);
    // A recurring window (e.g. a daily break) repeats every day from its start
    // date — its endDate is not a "last day" and may lie before the range.
    const days = a.isRecurring
        ? fill(t('stf.schedule.everyDayFrom'), { start })
        : a.endDate && a.endDate.slice(0, 10) !== a.startDate.slice(0, 10)
            ? fill(t('stf.schedule.dateRange'), { start, end })
            : start;
    const parts = [days];
    if (a.isFullDay) parts.push(t('stf.schedule.fullDay'));
    else if (a.startTime || a.endTime) parts.push(timeRange(a.startTime, a.endTime));
    const branch = a.branchId != null ? branchName(a.branchId) : '';
    if (branch) parts.push(branch);
    return parts.join(' · ');
}

const MySchedule: React.FC = () => {
    const { t, language } = useLanguage();
    const { data, isLoading, isError, refetch } = useStaffMySchedule();
    const me = useStaffMe();
    const branchName = (id: number): string => {
        const b = me.data?.branches?.find(x => x.id === id);
        if (b) return localizedName(b.name, b.nameAr, language);
        // Fall back to the names the weekly segments carry.
        for (const day of data?.weekly ?? []) {
            const seg = (day.segments ?? []).find(x => x.branchId === id);
            if (seg) return localizedName(seg.branchName, seg.branchNameAr, language);
        }
        return '';
    };

    const weekly = data?.weekly ?? [];
    const absences = data?.absences ?? [];
    const holidays = data?.holidays ?? [];

    return (
        <div className={s.page}>
            <header className={s.header}>
                <h1 className={s.title}>{t('stf.schedule.title')}</h1>
                <p className={s.subtitle}>{t('stf.schedule.subtitle')}</p>
            </header>

            {isLoading && (
                <div className={s.skeleton} data-testid="schedule-loading">
                    <span className={s.srOnly} role="status">{t('stf.loading')}</span>
                    <div className={s.skeletonBlock} aria-hidden="true" />
                </div>
            )}

            {isError && !data && (
                <div className={s.errorBox} role="alert">
                    <p>{t('stf.schedule.loadFailed')}</p>
                    <button type="button" className={s.primaryBtn} onClick={() => { void refetch(); }}>
                        {t('stf.retry')}
                    </button>
                </div>
            )}

            {data && (
                <>
                    <section className={s.card} aria-labelledby="schedule-weekly">
                        <h2 id="schedule-weekly" className={s.cardTitle}>{t('stf.schedule.weekly')}</h2>
                        {weekly.length === 0 ? (
                            <p className={s.empty}>{t('stf.schedule.weeklyEmpty')}</p>
                        ) : (
                            <dl className={s.week} data-testid="schedule-weekly">
                                {weekly.map(day => {
                                    const segments = day.segments ?? [];
                                    return (
                                        <div key={day.dayOfWeek} className={s.dayRow} data-working={day.isWorking}>
                                            <dt className={s.dayName}>{weekdayName(day.dayOfWeek, language)}</dt>
                                            <dd className={s.dayHours}>
                                                {!day.isWorking || segments.length === 0 ? (
                                                    <span className={s.muted}>{t('stf.schedule.notWorking')}</span>
                                                ) : (
                                                    <ul className={s.segments}>
                                                        {segments.map((seg, i) => {
                                                            const branch = localizedName(seg.branchName, seg.branchNameAr, language);
                                                            return (
                                                                <li key={`${seg.branchId ?? 'b'}-${seg.start}-${i}`} className={s.segment}>
                                                                    <span className={s.time}>{timeRange(seg.start, seg.end)}</span>
                                                                    {branch && <span className={s.muted}>{branch}</span>}
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                )}
                                            </dd>
                                        </div>
                                    );
                                })}
                            </dl>
                        )}
                    </section>

                    <section className={s.card} aria-labelledby="schedule-timeoff">
                        <h2 id="schedule-timeoff" className={s.cardTitle}>{t('stf.schedule.timeOff')}</h2>
                        {absences.length === 0 ? (
                            <p className={s.empty}>{t('stf.schedule.timeOffEmpty')}</p>
                        ) : (
                            <ul className={s.list} data-testid="schedule-absences">
                                {absences.map(a => (
                                    <li key={a.id} className={s.item}>
                                        <span className={s.itemTitle}>{absenceTypeLabel(a.type, t)}</span>
                                        <span className={s.muted}>{absenceWhen(a, language, t, branchName)}</span>
                                        {a.note && <span className={s.muted}><bdi dir="auto">{a.note}</bdi></span>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <section className={s.card} aria-labelledby="schedule-closures">
                        <h2 id="schedule-closures" className={s.cardTitle}>{t('stf.schedule.closures')}</h2>
                        {holidays.length === 0 ? (
                            <p className={s.empty}>{t('stf.schedule.closuresEmpty')}</p>
                        ) : (
                            <ul className={s.list} data-testid="schedule-closures">
                                {holidays.map((h, i) => {
                                    const name = localizedName(h.name, h.nameAr, language);
                                    const branches = (h.branchIds ?? []).map(branchName).filter(Boolean);
                                    return (
                                        <li key={`${h.date}-${i}`} className={s.item}>
                                            <span className={s.itemTitle}>{formatDayDate(h.date, language, DATE_OPTS)}</span>
                                            <span className={s.muted}>
                                                {[t('stf.schedule.closed'), name ? <bdi key="n" dir="auto">{name}</bdi> : null]
                                                    .filter(Boolean)
                                                    .map((part, idx) => <React.Fragment key={idx}>{idx > 0 && ' · '}{part}</React.Fragment>)}
                                            </span>
                                            {branches.length > 0 && (
                                                <span className={s.muted}>
                                                    {fill(t('stf.schedule.onlyBranches'), { branches: branches.join(language === 'ar' ? '، ' : ', ') })}
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

export default MySchedule;
